import { onMounted, onBeforeUnmount, ref, type Ref } from 'vue';
import type { AdStatus } from '@/features/native-ads/helpers/gam-native-helper';

export function useNativeAdIframeTracking({
	adContainer,
	adStatus,
	onClickout,
	onDestroyed,
}: {
	adContainer: Ref<HTMLElement | null>;
	adStatus: Ref<AdStatus>;
	onClickout: () => void;
	onDestroyed?: () => void;
}) {
	const isInFrame = { value: false };
	const timeoutRef = ref<ReturnType<typeof setTimeout> | null>(null);
	const observerRef = ref<MutationObserver | null>(null);
	const destructionObserverRef = ref<MutationObserver | null>(null);

	/**
	 * Get the render status of the native ad iframe
	 * Sets up click tracking listeners and waits for iframe load event
	 * Returns 'rendered' on success, 'failed' on timeout
	 *
	 * Without this, the GAM Slot Rendered event can fire before iframe is filled, causing an empty frame delay
	 */
	function getIframeRenderStatus(): Promise<AdStatus> {
		return new Promise(resolve => {
			// Clean up any previous state first
			cleanup();

			if (!adContainer.value) {
				console.log('[iframe] no ad container, returning failed');
				resolve('failed');
				return;
			}

			timeoutRef.value = setTimeout(() => {
				cleanup();
				resolve('failed');
			}, 7000);

			// Check if iframe already exists
			const existingIframe = adContainer.value.querySelector('iframe') as HTMLIFrameElement;
			if (existingIframe) {
				handleIframeFound(existingIframe);
				return;
			}

			observerRef.value = new MutationObserver(() => {
				const iframe = adContainer.value?.querySelector('iframe') as HTMLIFrameElement;

				if (iframe) {
					observerRef.value?.disconnect();
					handleIframeFound(iframe);
				}
			});

			observerRef.value.observe(adContainer.value, {
				childList: true,
				subtree: true,
			});

			function handleIframeFound(iframe: HTMLIFrameElement) {
				setupIframeListeners(iframe);
				watchForIframeRemoval(iframe);

				iframe.addEventListener(
					'load',
					() => {
						cleanup();
						resolve('rendered');
					},
					{ once: true }
				);
			}
		});
	}

	function watchForIframeRemoval(iframe: HTMLIFrameElement) {
		if (!onDestroyed || !adContainer.value) return;
		destructionObserverRef.value = new MutationObserver(() => {
			if (!adContainer.value?.contains(iframe)) {
				destructionObserverRef.value?.disconnect();
				destructionObserverRef.value = null;
				onDestroyed();
			}
		});
		destructionObserverRef.value.observe(adContainer.value, { childList: true, subtree: true });
	}

	/** Clean up timeout and observer refs */
	function cleanup() {
		if (timeoutRef.value) {
			clearTimeout(timeoutRef.value);
			timeoutRef.value = null;
		}
		if (observerRef.value) {
			observerRef.value.disconnect();
			observerRef.value = null;
		}
	}

	function setupIframeListeners(iframe: HTMLIFrameElement) {
		iframe.addEventListener('mouseover', () => {
			isInFrame.value = true;
		});

		iframe.addEventListener('mouseleave', () => {
			isInFrame.value = false;
		});
	}

	function handleBlur() {
		if (isInFrame.value && adStatus.value === 'rendered') {
			onClickout();
		}
	}

	onMounted(() => {
		window.addEventListener('blur', handleBlur);
	});

	onBeforeUnmount(() => {
		if (timeoutRef.value) {
			clearTimeout(timeoutRef.value);
			timeoutRef.value = null;
		}
		if (observerRef.value) {
			observerRef.value.disconnect();
			observerRef.value = null;
		}
		if (destructionObserverRef.value) {
			destructionObserverRef.value.disconnect();
			destructionObserverRef.value = null;
		}
		window.removeEventListener('blur', handleBlur);
	});

	return {
		getIframeRenderStatus,
	};
}
